import { useEffect, useState } from 'react'
import { api, tokens, type Tokens } from '../api'
import { Button, Card, Field, Input, Note, ErrorText } from '../components/ui'
import { Login } from './Login'

function tokenFromLink() {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? ''
}
export function InvitationAcceptance({ signedIn }: { signedIn: boolean }) {
  const [token] = useState(tokenFromLink)
  const [invite, setInvite] = useState<{ email: string; role: string; workspace_name: string; inviter_email: string; expires_at: string; requires_sign_in: boolean } | null>(null)
  const [error, setError] = useState<unknown>()
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [authenticated, setAuthenticated] = useState(signedIn)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [accountEmail, setAccountEmail] = useState('')
  useEffect(() => { api.post<typeof invite>('/api/auth/invitations/preview', { token }).then(setInvite).catch(setError) }, [token])
  useEffect(() => { if (authenticated) api.get<{ user: { email: string } }>('/api/auth/me').then(result => setAccountEmail(result.user.email)).catch(setError) }, [authenticated])
  async function accept(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError(undefined)
    try {
      const result = await api.post<{ tokens?: Tokens; workspace_id: string }>('/api/auth/invitations/accept', { token, name, password: authenticated ? undefined : password })
      if (result.tokens) tokens.set(result.tokens)
      localStorage.setItem('ugc.workspace', result.workspace_id)
      window.location.replace('/')
    } catch (err) { setError(err) } finally { setBusy(false) }
  }
  if (mode === 'existing' && !authenticated) return <div><Login onSignedIn={() => setAuthenticated(true)} /><div className="text-center"><Button onClick={() => setMode('new')}>Back to invitation</Button></div></div>
  const wrongAccount = authenticated && accountEmail && invite && accountEmail !== invite.email
  return <div className="mx-auto max-w-lg px-5 py-16"><Card>
    <h1 className="text-xl">Workspace invitation</h1>
    {error != null && <ErrorText error={error} />}
    {!invite && !error && <p>Loading invitation…</p>}
    {invite && <form onSubmit={accept} className="mt-5 space-y-4">
      <p><strong>{invite.inviter_email}</strong> invited <strong>{invite.email}</strong> to <strong>{invite.workspace_name}</strong> as {invite.role}.</p>
      <p className="text-sm text-ink-faint">Expires {new Date(invite.expires_at).toLocaleString()}</p>
      {wrongAccount ? <Note>You are signed in as {accountEmail}. Sign out and use {invite.email} to accept.</Note> : authenticated ? <Note>Accept to add this workspace to your account.</Note> : invite.requires_sign_in ? <Note>An account already exists for this email. Sign in below to accept the invitation.</Note> : <>
        <Field label="Your name"><Input required value={name} onChange={event => setName(event.target.value)} autoComplete="name" /></Field>
        <Field label="Choose a password" help="At least 12 characters"><Input type="password" required minLength={12} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" /></Field>
        <Field label="Confirm password"><Input type="password" required value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" /></Field>
      </>}
      <Button type="submit" variant="primary" disabled={busy || !!wrongAccount || (!authenticated && (invite.requires_sign_in || !name || password.length < 12 || password !== confirm))}>{busy ? 'Joining…' : 'Accept invitation'}</Button>
      {!authenticated && <Button type="button" onClick={() => setMode('existing')}>Already have an account? Sign in</Button>}
      {authenticated && <Button type="button" onClick={async () => { try { await api.post('/api/auth/logout'); tokens.clear(); setAuthenticated(false); setAccountEmail('') } catch (err) { setError(err) } }}>Sign out</Button>}
    </form>}
  </Card></div>
}

export function PasswordRecovery({ reset = false }: { reset?: boolean }) {
  const [token] = useState(tokenFromLink)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<unknown>()
  const [busy, setBusy] = useState(false)
  return <div className="mx-auto max-w-md px-5 py-16"><Card>
    <h1 className="text-xl">{reset ? 'Choose a new password' : 'Recover your account'}</h1>
    <form className="mt-5 space-y-4" onSubmit={async event => {
      event.preventDefault(); setBusy(true); setError(undefined)
      try {
        const result = await api.post<{ status: string }>(reset ? '/api/auth/password/reset' : '/api/auth/password/forgot', reset ? { token, password } : { email })
        setMessage(result.status)
        if (reset) { tokens.clear(); setPassword(''); setConfirm('') }
      } catch (err) { setError(err) } finally { setBusy(false) }
    }}>
      {error != null && <ErrorText error={error} />}
      {message && <Note>{message}</Note>}
      {!message && <>
        {reset ? <>
          <Field label="New password" help="At least 12 characters"><Input type="password" required minLength={12} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" /></Field>
          <Field label="Confirm new password"><Input type="password" required value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" /></Field>
        </> : <Field label="Email"><Input type="email" required value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" /></Field>}
        <Button type="submit" variant="primary" disabled={busy || (reset && (password.length < 12 || password !== confirm))}>{busy ? 'Working…' : reset ? 'Reset password' : 'Send reset link'}</Button>
      </>}
      <a className="block text-sm underline" href="/">Back to sign in</a>
    </form>
  </Card></div>
}
